import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import { DateTime } from 'luxon';

const getAsDateTime = (date: string | DateTime | Date): DateTime => {
  if (typeof date === 'string') return DateTime.fromISO(date);
  if (date instanceof Date) return DateTime.fromJSDate(date);
  return date;
};

type AcceptedDateValues = DateTime | Date | string;
type Interval = { started_at: AcceptedDateValues; ended_at: AcceptedDateValues };
type FromToInterval = { from: AcceptedDateValues; to: AcceptedDateValues };
type RequiredIntervalDates = Interval | FromToInterval;
export type StandardInterval<T> = Omit<T, 'started_at' | 'ended_at'> & {
  started_at: DateTime;
  ended_at: DateTime;
};

type RemoveOverlapOptions = {
  requireMatchOnKeys?: string[];
};

/**
 * Takes a generic interval (from/to or started_at/ended_at) and eliminates any overlaps with preoccupied intervals,
 * returning an array of non-overlapping intervals enriched with started_at/ended_at as DateTime.
 *
 * @param interval The interval to be modified/adjusted based on...
 * @param preoccupiedIntervals The `interval` will remove any overlaps from these `preoccupiedIntervals`
 * @param options.requireMatchOnKeys If provided, the function will only remove overlaps if the keys match on the interval vs the occupied interval.
 * @returns Array of mutated `intervals` that exclude any time overlaps with `preoccupiedIntervals`.
 */
export const removeOverlapFromGenericInterval = <T extends RequiredIntervalDates>(
  interval: T,
  preoccupiedIntervals: Array<RequiredIntervalDates>,
  { requireMatchOnKeys }: RemoveOverlapOptions = {}
): Array<StandardInterval<T>> => {
  const intervalStartedAt = getAsDateTime('from' in interval ? interval.from : interval.started_at);
  const intervalEndedAt = getAsDateTime('to' in interval ? interval.to : interval.ended_at);

  // Sort preoccupied intervals by start time to process them in order
  const sortedPreoccupied = preoccupiedIntervals
    .map(p => ({
      ...p,
      started_at: getAsDateTime('from' in p ? p.from : p.started_at),
      ended_at: getAsDateTime('to' in p ? p.to : p.ended_at),
    }))
    .sort((a, b) => a.started_at.toMillis() - b.started_at.toMillis());

  let currentStart = intervalStartedAt;
  const nonOverlappingIntervals: Array<StandardInterval<T>> = [];

  // Iterate over each preoccupied interval to find non-overlapping parts
  for (const occupied of sortedPreoccupied) {
    // No more non-overlapping parts possible, exit early
    if (currentStart >= intervalEndedAt) break;

    // If we have a requireMatchOnKeys option, we need to check if the keys match on the
    // interval vs the occupied interval. If they don't match, we skip this occupied interval.
    if (requireMatchOnKeys?.length) {
      const allKeysMatch = requireMatchOnKeys.every(key => {
        const intervalKey = get(interval, key);
        const occupiedKey = get(occupied, key);
        return isEqual(intervalKey, occupiedKey);
      });

      if (!allKeysMatch) continue;
    }

    // Current start is before the occupied interval, add non-overlapping part
    if (currentStart < occupied.started_at) {
      nonOverlappingIntervals.push({
        ...interval,
        started_at: currentStart,
        ended_at: occupied.started_at < intervalEndedAt ? occupied.started_at : intervalEndedAt,
      });
    }

    // Update current start to the end of the overlapping interval, if within the original interval
    currentStart = occupied.ended_at > currentStart ? occupied.ended_at : currentStart;
  }

  // Check for a final non-overlapping part after the last preoccupied interval
  if (currentStart < intervalEndedAt) {
    nonOverlappingIntervals.push({
      ...interval,
      started_at: currentStart,
      ended_at: intervalEndedAt,
    });
  }

  return nonOverlappingIntervals;
};
